import {addMonths, getMonth, getYear, isAfter, isBefore, isSameMonth, startOfMonth} from 'date-fns';
import {Box, HStack} from 'platform/foundation';
import {useLocale} from 'platform/locale';

import {isNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {Choice} from '../../../Choice/Choice';
import type {FocusZoneType} from '../../../DatePicker/hooks/useDatePickerFocusTrap';
import {IconButton} from '../../../IconButton/IconButton';
import {getMemoizedMonthChoiceOptions} from '../../utils/getMemoizedMonthChoiceOptions';
import {getMemoizedYearChoiceOptions} from '../../utils/getMemoizedYearChoiceOptions';

interface CalendarControlProps extends TestIdProps {
  activeMonth: Date;
  minDate?: Date;
  maxDate?: Date;
  onChangeActiveMonth: (date: Date) => void;
  currentFocusZone?: FocusZoneType | null;
}

export function CalendarControl(props: CalendarControlProps) {
  const {language} = useLocale();

  const yearChoiceOptions = getMemoizedYearChoiceOptions(props.minDate, props.maxDate);
  const monthChoiceOptions = getMemoizedMonthChoiceOptions(
    props.activeMonth.getFullYear(),
    props.minDate,
    props.maxDate,
    language
  );

  const onMoveForward = () => props.onChangeActiveMonth(addMonths(props.activeMonth, 1));
  const canGoForward =
    !props.maxDate || isAfter(startOfMonth(props.maxDate), startOfMonth(props.activeMonth));

  const onMoveBackward = () => props.onChangeActiveMonth(addMonths(props.activeMonth, -1));
  const canGoBackward =
    !props.minDate || isBefore(startOfMonth(props.minDate), startOfMonth(props.activeMonth));

  // Default to -1 (out of the tab order) when no zone is managed. Consumers like the
  // standalone Calendar and DateRangePicker never pass currentFocusZone and drive these
  // controls themselves, so they must stay non-tabbable — only DatePicker's focus trap
  // promotes the active control to tabindex 0.
  const getTabIndex = (focusZone: FocusZoneType) => {
    if (isNil(props.currentFocusZone)) {
      return -1;
    }

    return props.currentFocusZone === focusZone ? 0 : -1;
  };

  const onChangeMonth = (value: string | number | string[] | null) => {
    if (typeof value !== 'string') {
      return null;
    }
    props.onChangeActiveMonth(new Date(props.activeMonth.getFullYear(), Number.parseInt(value), 1));
  };

  const onChangeYear = (value: string | number | string[] | null) => {
    if (typeof value !== 'string') {
      return;
    }
    const newActiveMonth = new Date(Number.parseInt(value), props.activeMonth.getMonth(), 1);

    if (
      props.minDate &&
      isBefore(newActiveMonth, props.minDate) &&
      !isSameMonth(newActiveMonth, props.minDate)
    ) {
      return props.onChangeActiveMonth(startOfMonth(props.minDate));
    }

    if (
      props.maxDate &&
      isAfter(newActiveMonth, props.maxDate) &&
      !isSameMonth(newActiveMonth, props.maxDate)
    ) {
      return props.onChangeActiveMonth(startOfMonth(props.maxDate));
    }

    props.onChangeActiveMonth(newActiveMonth);
  };

  return (
    <HStack spacing={2}>
      <IconButton
        priority="tertiary"
        icon="navigation/chevron_left"
        onClick={onMoveBackward}
        isDisabled={!canGoBackward}
        tabIndex={getTabIndex('previous-month-button')}
        data-testid={suffixTestId('buttonPreviousMonth', props)}
      />
      <Box flexGrow={1} maxWidth={40}>
        <Choice
          value={`${getMonth(props.activeMonth)}`}
          options={monthChoiceOptions}
          onChange={onChangeMonth}
          isNotClearable
          menuInPortal
          tabIndex={getTabIndex('month-select')}
          data-testid={suffixTestId('choiceMonth', props)}
        />
      </Box>
      <Box width={22}>
        <Choice
          value={`${getYear(props.activeMonth)}`}
          options={yearChoiceOptions}
          onChange={onChangeYear}
          isNotClearable
          menuInPortal
          tabIndex={getTabIndex('year-select')}
          data-testid={suffixTestId('choiceYear', props)}
        />
      </Box>
      <IconButton
        priority="tertiary"
        icon="navigation/chevron_right"
        onClick={onMoveForward}
        isDisabled={!canGoForward}
        tabIndex={getTabIndex('next-month-button')}
        data-testid={suffixTestId('buttonNextMonth', props)}
      />
    </HStack>
  );
}
